// retryLink.ts
import { ErrorCodes, IApiArgument } from "config";
import { delay } from "utils/delay";
import { isPollingError } from "utils/error";
import { buildPollingInterval, getRetrySources, hasExpiredRetryTimeout } from "utils/retryQuery";

import { ApolloLink, FetchResult, Operation } from "@apollo/client";
import { GraphQLError, GraphQLFormattedError } from "graphql";
import { DateTime } from "luxon";
import { Observable, Subscription } from "rxjs";

export interface RetryLinkConfig {
  retryArguments?: IApiArgument[];
}

export const RETRY_TIMEOUT_ERROR: ErrorCodes = "RETRY_TIMEOUT_EXCEEDED" as const;

const getRetryArguments = (operation: Operation): IApiArgument[] => {
  const context = operation.getContext() as RetryLinkConfig;
  const operationArguments = context.retryArguments || [];
  const variableArguments = operation.variables?.retryArguments || [];
  const allArguments = [...operationArguments, ...variableArguments];
  return allArguments;
};

export const retryLink = new ApolloLink(
  (operation: Operation, forward: ApolloLink.ForwardFunction) => {
    const retryArguments = getRetryArguments(operation);
    const retryConfig = getRetrySources(retryArguments);
    const { retryTimeout = 300 } = retryConfig;
    const startTime = DateTime.local();

    return new Observable<FetchResult>(observer => {
      let retryCount = 0;
      let subscriber: Subscription | null = null;

      const attempt = () => {
        subscriber = forward(operation).subscribe({
          next: result => {
            const errors: readonly GraphQLFormattedError[] = result.errors || [];
            const hasPollingError = errors.some(err => isPollingError(err as GraphQLError));
            const expired = hasExpiredRetryTimeout(startTime, retryTimeout);

            // If not a polling error, fail the operation
            // immediately and thrown the error to the link chain
            // --------------------------------------
            // If we have exceeded the retry timeout, throw an error
            // to prevent any subsequent retry attempts
            // --------------------------------------
            // Otherwise, we will apply a delay (linear growth) and
            // retry the operation.

            if (!hasPollingError) {
              observer.next(result);
              observer.complete();
            } else if (expired) {
              observer.error(new GraphQLError(RETRY_TIMEOUT_ERROR));
            } else {
              const wait = buildPollingInterval({ retryCount, ...retryConfig });
              retryCount++;
              delay(wait).then(attempt);
            }
          },
          error: error => {
            observer.error(error);
          },
        });
      };

      attempt();

      return () => subscriber && subscriber.unsubscribe();
    });
  }
);
